import React, { Children } from 'react'
import PropTypes from 'prop-types'
import cx from 'classnames'
import styles from './panel.module.css'

const propTypes = {
    CloseComponent: PropTypes.node,
    ExpandComponent: PropTypes.node,
    // eslint-disable-next-line react/forbid-prop-types
    children: PropTypes.any,
    className: PropTypes.string,
    classNamePanelHeaderButtonClose: PropTypes.string,
    classNamePanelHeaderButtonExpand: PropTypes.string,
    isClosable: PropTypes.bool,
    isExpandable: PropTypes.bool,
    onClose: PropTypes.func,
    onExpand: PropTypes.func,
    // eslint-disable-next-line react/forbid-prop-types
    style: PropTypes.object
}

const defaultProps = {
    isClosable: true,
    isExpandable: false
}

/**
 * Render a panel component.
 *
 * @example <caption>Simple usage</caption>
 * <code>
 * <Panel>
 *      <PanelHeader>
 *          Header Content
 *      </PanelHeader>
 *      <PanelContent>
 *          Main Content
 *      </PanelContent>
 * </Panel>
 * </code>
 *
 * @param {Object} [props] - The current component properties
 * @param {ReactElement|string]number} [props.children] - The children to be rendered
 * @param {string} [props.className] - Classname to be added
 * @param {string} [props.classNamePanelHeaderButtonClose] - Classname for the panel header close btn
 * @param {string} [props.classNamePanelHeaderButtonExpand] - Classname for the panel header expand btn
 * @param {ReactElement} [props.CloseComponent] - A custom close button component
 * @param {ReactElement} [props.ExpandComponent] - A custom expand button component
 * @param {boolean} [props.isClosable=true] - Add CloseIcon to header or not
 * @param {boolean} [props.isExpandable=false] - Show expand button or not
 * @param {Function} [props.onClose] - Callback when close button get's clicked
 * @param {Function} [props.onExpand] - Callback when expand button get's clicked
 * @param {Object} [props.style] - Addional style attribute contents
 * @returns {ReactElement}
 */
function Panel(props) {
    const {
        CloseComponent,
        ExpandComponent,
        children,
        className,
        classNamePanelHeaderButtonClose,
        classNamePanelHeaderButtonExpand,
        isClosable,
        isExpandable,
        onClose,
        onExpand,
        style
    } = props

    if (Children.count(children) !== 2) {
        throw new Error('A Panel needs to have a PanelHeader and a PanelContent!')
    }

    const panelHeader = React.cloneElement(children[0], {
        CloseComponent,
        ExpandComponent,
        classNamePanelHeaderButtonClose,
        classNamePanelHeaderButtonExpand,
        isClosable,
        isExpandable,
        onClose,
        onExpand
    })
    const panelContent = React.cloneElement(children[1])
    const classNameWrapper = cx(styles['panel'], className, 't3sel-panel')

    return (
        <section className={classNameWrapper} style={style}>
            {panelHeader}
            {panelContent}
        </section>
    )
}

Panel.propTypes = propTypes
Panel.defaultProps = defaultProps

export default Panel
